import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomerType } from "store/customers/add-new-customer/add-new-customer-types";
import cn from "classnames";
import {
    ISearchService,
    ISftpUserWithIpAddresses,
    OcrConfiguration,
    Features,
    TenantFeatureGroup,
    TenantMailerService,
    OcrMethods,
    RecognitionPhase,
} from "store/environments/tenants/tenant-configurations/tenant-configurations-types";
import {
    Button,
    Checkbox,
    Form,
    InputGroup,
    InputNumber,
    SelectPicker,
    Input,
    Message,
    HStack,
} from "rsuite";
import ArrowUpLineIcon from "@rsuite/icons/ArrowUpLine";
import ArrowDownLineIcon from "@rsuite/icons/ArrowDownLine";

import { isTenantFeatureSettingWithTypeEnabled } from "utils/tenant-workflows/tenant-workflows-helper";

import {
    ITenantFeatureSettingWorkFlow,
    TenantFeatureSettingWorkFlowType,
    WorkflowModalType,
} from "utils/tenant-workflows/tenant-workflows-types";

import {
    ConfluenceLinks,
    MicrosoftLinks,
} from "store/information-links/information-links-types";
import { IInvoiceAdditionalHeaderInformation } from "store/environments/tenants/invoices/invoice-additional-header-information/invoice-additional-header-information-types";

import ProcessModalButton from "components/WorkflowLog/ProcessModalButton/ProcessModalButton";
import Collapsible from "components/Common/Collapsible/Collapsible";
import TenantFeatureSettingsCheckbox from "../TenantFeatureSettingsCheckbox/TenantFeatureSettingsCheckbox";
import SftpIpAddressesContainer from "../SftpIpAddresses/SftpIpAddressesContainer";

import "./FeatureGroups.less";

interface ComponentProps {
    features: Features;
    ocrConfiguration: OcrConfiguration;
    confluenceDocumentLinks?: ConfluenceLinks | null;
    microsoftDocumentLinks?: MicrosoftLinks | null;
    customerType: CustomerType;
    featureSettingWorkFlows: ITenantFeatureSettingWorkFlow[] | null;
    disabledSettingNames: string[];
    allWorkflows: {
        workflowType: TenantFeatureSettingWorkFlowType;
        getFeature: (feature: Features) => boolean;
        text: string;
        helpText: string;
        value: string;
        checked: boolean | undefined;
        isDisabled: boolean;
        isAvailableForAx: boolean;
        group: TenantFeatureGroup;
    }[];
    sftpUserWithIpAddresses: ISftpUserWithIpAddresses;
    areSftpAddressesValid: boolean;
    workflowFromXmlField: IInvoiceAdditionalHeaderInformation | null;
    isModifySearchDisabled: boolean;
    searchServices: ISearchService[];
    linkedSearchService: string | null;
    setSftpUserWithIpAddresses: (
        sftpUserWithIpAddresses: ISftpUserWithIpAddresses
    ) => void;
    setSftpAddressesValid: (isValid: boolean) => void;
    handleTenantFeatureChange: (
        feature: keyof Features,
        value: string | number | boolean
    ) => void;
    handleOcrConfigurationChange: (
        name: keyof OcrConfiguration,
        value: OcrMethods
    ) => void;
    handleInvoiceAdditionalHeaderInformation: (
        name: keyof IInvoiceAdditionalHeaderInformation,
        value: string
    ) => void;
    handleUpdateClick: () => void;
    handleRecreateClick: () => void;
    handleReindexClick: () => void;
    handleSeedClick: () => void;
    setLinkedSearchService: (searchService: string) => void;
    setIsUnsavedChangeAvailable: (isChangesAvailable: boolean) => void;
    setDataFetched: (isFetched: false) => void;
}

const TenantFeatureGroupsMapping = [
    TenantFeatureGroup.automaticVendorAssigment,
    TenantFeatureGroup.mlWorkflow,
    TenantFeatureGroup.mlCompany,
    TenantFeatureGroup.mlCoding,
    TenantFeatureGroup.fromXml,
    TenantFeatureGroup.autoTtp,
    TenantFeatureGroup.poSettings,
    TenantFeatureGroup.vendor,
    TenantFeatureGroup.vendorSimilaritySearch,
    TenantFeatureGroup.sftp,
    TenantFeatureGroup.ocr,
    TenantFeatureGroup.search,
    TenantFeatureGroup.other,
];

const FeatureGroups: React.FC<ComponentProps> = ({
    features,
    ocrConfiguration,
    confluenceDocumentLinks,
    microsoftDocumentLinks,
    customerType,
    featureSettingWorkFlows,
    disabledSettingNames,
    allWorkflows,
    sftpUserWithIpAddresses,
    areSftpAddressesValid,
    workflowFromXmlField,
    isModifySearchDisabled,
    searchServices,
    linkedSearchService,
    setSftpUserWithIpAddresses,
    setSftpAddressesValid,
    handleTenantFeatureChange,
    handleOcrConfigurationChange,
    handleInvoiceAdditionalHeaderInformation,
    handleUpdateClick,
    handleRecreateClick,
    handleReindexClick,
    handleSeedClick,
    setLinkedSearchService,
    setIsUnsavedChangeAvailable,
    setDataFetched,
}: ComponentProps) => {
    const { t } = useTranslation();
    const [expandedStates, setExpandedStates] = useState<boolean[]>(
        new Array(TenantFeatureGroupsMapping.length).fill(false)
    );
    const allExpanded = expandedStates.every((state) => state);
    const buttonText = allExpanded
        ? t("tenantConfig.featureConfigurations.group.buttonCollapse")
        : t("tenantConfig.featureConfigurations.group.buttonExpand");
    const upperIcon = allExpanded ? <ArrowDownLineIcon /> : <ArrowUpLineIcon />;
    const lowerIcon = allExpanded ? <ArrowUpLineIcon /> : <ArrowDownLineIcon />;

    const searchServiceSelectionWorkflow =
        featureSettingWorkFlows?.find(
            (workflow) =>
                workflow.type ===
                TenantFeatureSettingWorkFlowType.SearchServiceSelection
        ) ?? null;

    const similarityVendorRecognitionWorkflow =
        featureSettingWorkFlows?.find(
            (workflow) =>
                workflow.type ===
                TenantFeatureSettingWorkFlowType.SimilarityVendorRecognition
        ) ?? null;

    const isSearchSettingsDisabled = !(searchServiceSelectionWorkflow !== null
        ? !disabledSettingNames.includes(
              searchServiceSelectionWorkflow.featureName
          ) &&
          isTenantFeatureSettingWithTypeEnabled(
              featureSettingWorkFlows,
              TenantFeatureSettingWorkFlowType.SearchServiceSelection
          )
        : true);

    const isVendorSimilaritySearchDisabled =
        !features.vectorVendorRecognitionEnabled ||
        !(similarityVendorRecognitionWorkflow !== null
            ? !disabledSettingNames.includes(
                  similarityVendorRecognitionWorkflow.featureName
              ) &&
              isTenantFeatureSettingWithTypeEnabled(
                  featureSettingWorkFlows,
                  TenantFeatureSettingWorkFlowType.SimilarityVendorRecognition
              )
            : true);

    const featureFlagTypeToSettingMapping = [
        {
            name: t(
                "tenantConfig.featureConfigurations.updateInvoiceWithPoData.title"
            ),
            value: "updateInvoiceWithPoData",
            tooltip: t(
                "tenantConfig.featureConfigurations.updateInvoiceWithPoData.description"
            ),
            isAvailableForAx: false,
            isEnabled: features?.updateInvoiceWithPoData,
            confluenceLink: null,
            group: TenantFeatureGroup.poSettings,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.poPurchStatusInvoicedFilteringEnabled.title"
            ),
            value: "poPurchStatusInvoicedFilteringEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.poPurchStatusInvoicedFilteringEnabled.description"
            ),
            isAvailableForAx: false,
            isEnabled: features?.poPurchStatusInvoicedFilteringEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.poSettings,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.dbaDefaultApprovers.title"
            ),
            value: "dbaDefaultApprovers",
            tooltip: t(
                "tenantConfig.featureConfigurations.dbaDefaultApprovers.description"
            ),
            isAvailableForAx: false,
            isEnabled: features?.dbaDefaultApprovers,
            confluenceLink: null,
            group: TenantFeatureGroup.other,
        },
        {
            name: t("tenantConfig.featureConfigurations.dueDateFromPO.title"),
            value: "dueDateFromPO",
            tooltip: t(
                "tenantConfig.featureConfigurations.dueDateFromPO.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.dueDateFromPO,
            confluenceLink: null,
            group: TenantFeatureGroup.poSettings,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.fillMissingProjectDimensionsFromVendorDefaults.title"
            ),
            value: "fillMissingProjectDimensionsFromVendorDefaults",
            tooltip: t(
                "tenantConfig.featureConfigurations.fillMissingProjectDimensionsFromVendorDefaults.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.fillMissingProjectDimensionsFromVendorDefaults,
            confluenceLink: null,
            group: TenantFeatureGroup.other,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.vatRegistrationNumberValidationEnabled.title"
            ),
            value: "vatRegistrationNumberValidationEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.vatRegistrationNumberValidationEnabled.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.vatRegistrationNumberValidationEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.other,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.poStatusFilteringEnabled.title"
            ),
            value: "poStatusFilteringEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.poStatusFilteringEnabled.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.poStatusFilteringEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.poSettings,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.vendorSearchByAddress.title"
            ),
            value: "vendorSearchByAddress",
            tooltip: t(
                "tenantConfig.featureConfigurations.vendorSearchByAddress.description"
            ),
            isAvailableForAx: false,
            isEnabled: features?.vendorSearchByAddress,
            confluenceLink: null,
            group: TenantFeatureGroup.vendor,
        },
        {
            name: t("tenantConfig.featureConfigurations.autoTTPEnabled.title"),
            value: "autoTTPEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.autoTTPEnabled.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.autoTTPEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.autoTtp,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.remitToAddressEnabled.title"
            ),
            value: "remitToAddressEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.remitToAddressEnabled.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.remitToAddressEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.other,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.filterDimensions.title"
            ),
            value: "dimensionDataLiveFiltering",
            tooltip: t(
                "tenantConfig.featureConfigurations.filterDimensions.description"
            ),
            isAvailableForAx: false,
            isEnabled: features?.dimensionDataLiveFiltering,
            confluenceLink: null,
            group: TenantFeatureGroup.other,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.positionHierarchy.title"
            ),
            value: "positionHierarchyEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.positionHierarchy.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.positionHierarchyEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.other,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.invoiceEscalationEnabled.title"
            ),
            value: "invoiceEscalationEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.invoiceEscalationEnabled.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.invoiceEscalationEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.other,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.vatAmountValidation.title"
            ),
            value: "vatAmountValidation",
            tooltip: t(
                "tenantConfig.featureConfigurations.vatAmountValidation.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.vatAmountValidation,
            confluenceLink: null,
            group: TenantFeatureGroup.other,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.currentPostingDate.title"
            ),
            value: "currentPostingDate",
            tooltip: t(
                "tenantConfig.featureConfigurations.currentPostingDate.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.currentPostingDate,
            confluenceLink: null,
            group: TenantFeatureGroup.other,
        },
        {
            name: t("tenantConfig.featureConfigurations.poLinkEnabled.title"),
            value: "poLinkEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.poLinkEnabled.description"
            ),
            isAvailableForAx: false,
            isEnabled: features?.poLinkEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.poSettings,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.waitForPrEnabled.title"
            ),
            value: "waitForPrEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.waitForPrEnabled.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.waitForPrEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.poSettings,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.bankAccountComparisonLogicEnabled.title"
            ),
            value: "bankAccountComparisonLogicEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.bankAccountComparisonLogicEnabled.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.bankAccountComparisonLogicEnabled,
            confluenceLink: confluenceDocumentLinks?.bankAccountValidation,
            group: TenantFeatureGroup.other,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.useImportedSigningLimits.title"
            ),
            value: "useImportedSigningLimits",
            tooltip: t(
                "tenantConfig.featureConfigurations.useImportedSigningLimits.description"
            ),
            isAvailableForAx: false,
            isEnabled: features?.useImportedSigningLimits,
            confluenceLink: null,
            group: TenantFeatureGroup.other,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.codingDeliveryAddressEnabled.title"
            ),
            value: "codingDeliveryAddressEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.codingDeliveryAddressEnabled.description"
            ),
            isAvailableForAx: false,
            isEnabled: features?.codingDeliveryAddressEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.other,
        },
        {
            name: t("tenantConfig.featureConfigurations.ssrEnabled.title"),
            value: "ssrEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.ssrEnabled.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.ssrEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.other,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.studioManualAcceptanceEnabled.title"
            ),
            value: "studioManualAcceptanceEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.studioManualAcceptanceEnabled.description"
            ),
            isAvailableForAx: false,
            isEnabled: features?.studioManualAcceptanceEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.other,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.recurringInvoicesEnabled.title"
            ),
            value: "recurringInvoicesEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.recurringInvoicesEnabled.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.recurringInvoicesEnabled,
            confluenceLink: confluenceDocumentLinks?.recurringInvoices,
            group: TenantFeatureGroup.other,
        },
        {
            name: t("tenantConfig.featureConfigurations.axTurboSwitch.title"),
            value: "axTurboSwitch",
            tooltip: t(
                "tenantConfig.featureConfigurations.axTurboSwitch.description"
            ),
            isAvailableForAx: false,
            isEnabled: features?.axTurboSwitch,
            confluenceLink: null,
            group: TenantFeatureGroup.other,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.companyPredictionEnabled.title"
            ),
            value: "companyPredictionEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.companyPredictionEnabled.description"
            ),
            isAvailableForAx: false,
            isEnabled: features?.companyPredictionEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.mlCompany,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.poChargesGroupEnabled.title"
            ),
            value: "poChargesGroupEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.poChargesGroupEnabled.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.poChargesGroupEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.vendor,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.vendorPredictionEnabled.title"
            ),
            value: "vendorPredictionEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.vendorPredictionEnabled.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.vendorPredictionEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.automaticVendorAssigment,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.searchVendorBySearchName.title"
            ),
            value: "searchVendorBySearchName",
            tooltip: t(
                "tenantConfig.featureConfigurations.searchVendorBySearchName.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.searchVendorBySearchName,
            confluenceLink: null,
            group: TenantFeatureGroup.vendor,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.procurementLineEnabled.title"
            ),
            value: "procurementLineEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.procurementLineEnabled.description"
            ),
            isAvailableForAx: false,
            isEnabled: features?.procurementLineEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.poSettings,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.purchaseAgreementEnabled.title"
            ),
            value: "purchaseAgreementEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.purchaseAgreementEnabled.description"
            ),
            isAvailableForAx: false,
            isEnabled: features?.purchaseAgreementEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.poSettings,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.ocrErrorReportingEnabled.title"
            ),
            value: "ocrErrorReportingEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.ocrErrorReportingEnabled.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.ocrErrorReportingEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.ocr,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.contractsEnabled.title"
            ),
            value: "contractsEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.contractsEnabled.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.contractsEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.other,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.codingFromXMLEnabled.title"
            ),
            value: "codingFromXMLEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.codingFromXMLEnabled.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.codingFromXMLEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.fromXml,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.postingDateFromXmlEnabled.title"
            ),
            value: "postingDateFromXmlEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.postingDateFromXmlEnabled.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.postingDateFromXmlEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.fromXml,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.amazonInvoicesEnabled.title"
            ),
            value: "amazonInvoicesEnabled",
            tooltip: t(
                "tenantConfig.featureConfigurations.amazonInvoicesEnabled.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.amazonInvoicesEnabled,
            confluenceLink: null,
            group: TenantFeatureGroup.other,
        },
        {
            name: t("tenantConfig.featureConfigurations.workflowFromXml.title"),
            value: "workflowFromXml",
            tooltip: t(
                "tenantConfig.featureConfigurations.workflowFromXml.description"
            ),
            isAvailableForAx: true,
            isEnabled: features?.workflowFromXml,
            confluenceLink: null,
            group: TenantFeatureGroup.fromXml,
        },
    ];

    const featureInputTypeToSettingMapping = [
        {
            name: t(
                "tenantConfig.featureConfigurations.workflowFromXmlField.title"
            ),
            type: Input,
            value: "xPath",
            selectedValue: workflowFromXmlField?.xPath,
            changeHandler: handleInvoiceAdditionalHeaderInformation,
            group: TenantFeatureGroup.fromXml,
            disabled: !features?.workflowFromXml,
            isAvailableForAx: true,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.autoTTPMaxBatchSize.title"
            ),
            type: InputNumber,
            value: "autoTTPMaxBatchSize",
            helpText: t(
                "tenantConfig.featureConfigurations.autoTTPMaxBatchSize.description"
            ),
            numberValue: features?.autoTTPMaxBatchSize,
            changeHandler: handleTenantFeatureChange,
            microsoftLink: microsoftDocumentLinks?.serviceProtectionApiLimits,
            group: TenantFeatureGroup.autoTtp,
            isAvailableForAx: true,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.userSessionIdleTimeout.title"
            ),
            type: InputNumber,
            value: "userSessionIdleTimeoutInMinutes",
            helpText: t(
                "tenantConfig.featureConfigurations.userSessionIdleTimeout.description"
            ),
            numberValue: features?.userSessionIdleTimeoutInMinutes,
            changeHandler: handleTenantFeatureChange,
            group: TenantFeatureGroup.other,
            inputAddon: true,
            isAvailableForAx: true,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.workflowPredictionImportMinScore.title"
            ),
            type: InputNumber,
            value: "workflowPredictionImportMinScore",
            helpText: t(
                "tenantConfig.featureConfigurations.workflowPredictionImportMinScore.description"
            ),
            numberValue: features?.workflowPredictionImportMinScore,
            changeHandler: handleTenantFeatureChange,
            group: TenantFeatureGroup.mlWorkflow,
            isAvailableForAx: true,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.companyPredictionImportMinScore.title"
            ),
            type: InputNumber,
            value: "companyPredictionImportMinScore",
            helpText: t(
                "tenantConfig.featureConfigurations.companyPredictionImportMinScore.description"
            ),
            numberValue: features?.companyPredictionImportMinScore,
            changeHandler: handleTenantFeatureChange,
            group: TenantFeatureGroup.mlCompany,
            isAvailableForAx: true,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.codingPredictionImportMinScore.title"
            ),
            type: InputNumber,
            value: "codingPredictionImportMinScore",
            helpText: t(
                "tenantConfig.featureConfigurations.codingPredictionImportMinScore.description"
            ),
            numberValue: features?.codingPredictionImportMinScore,
            changeHandler: handleTenantFeatureChange,
            group: TenantFeatureGroup.mlCoding,
            isAvailableForAx: true,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.vendorPredictionVendorAddressMinScore.title"
            ),
            type: InputNumber,
            value: "vendorPredictionVendorAddressMinScore",
            helpText: t(
                "tenantConfig.featureConfigurations.vendorPredictionVendorAddressMinScore.description"
            ),
            numberValue: features?.vendorPredictionVendorAddressMinScore,
            changeHandler: handleTenantFeatureChange,
            group: TenantFeatureGroup.automaticVendorAssigment,
            isAvailableForAx: true,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.vendorPredictionVendorNameMinScore.title"
            ),
            type: InputNumber,
            value: "vendorPredictionVendorNameMinScore",
            helpText: t(
                "tenantConfig.featureConfigurations.vendorPredictionVendorNameMinScore.description"
            ),
            numberValue: features?.vendorPredictionVendorNameMinScore,
            changeHandler: handleTenantFeatureChange,
            group: TenantFeatureGroup.automaticVendorAssigment,
            isAvailableForAx: true,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.vectorVendorRecognitionMinScore.title"
            ),
            type: InputNumber,
            value: "vectorVendorRecognitionMinScore",
            helpText: t(
                "tenantConfig.featureConfigurations.vectorVendorRecognitionMinScore.description"
            ),
            numberValue: features?.vectorVendorRecognitionMinScore,
            changeHandler: handleTenantFeatureChange,
            group: TenantFeatureGroup.vendorSimilaritySearch,
            disabled: isVendorSimilaritySearchDisabled,
            isAvailableForAx: true,
        },
    ];

    const ocrMethods = [
        {
            label: t(
                "tenantConfig.featureConfigurations.ocrConfigurations.labels.photonSync"
            ),
            value: OcrMethods.PhotonSync,
        },
        {
            label: t(
                "tenantConfig.featureConfigurations.ocrConfigurations.labels.photonHitl"
            ),
            value: OcrMethods.PhotonHitl,
        },
        {
            label: t(
                "tenantConfig.featureConfigurations.ocrConfigurations.labels.llm"
            ),
            value: OcrMethods.Llm,
        },
        {
            label: t(
                "tenantConfig.featureConfigurations.ocrConfigurations.labels.passthroughOnly"
            ),
            value: OcrMethods.PassthroughOnly,
        },
    ];

    const mailerServiceData = [
        {
            label: t(
                "tenantConfig.featureConfigurations.mailerService.labels.notSet"
            ),
            value: TenantMailerService.NotSet,
        },
        {
            label: t(
                "tenantConfig.featureConfigurations.mailerService.labels.smtpClient"
            ),
            value: TenantMailerService.SmtpClient,
        },
        {
            label: t(
                "tenantConfig.featureConfigurations.mailerService.labels.sendGrid"
            ),
            value: TenantMailerService.SendGrid,
        },
        {
            label: t(
                "tenantConfig.featureConfigurations.mailerService.labels.pepipost"
            ),
            value: TenantMailerService.Pepipost,
        },
        {
            label: t(
                "tenantConfig.featureConfigurations.mailerService.labels.postmark"
            ),
            value: TenantMailerService.Postmark,
        },
        {
            label: t(
                "tenantConfig.featureConfigurations.mailerService.labels.office365Relay"
            ),
            value: TenantMailerService.Office365Relay,
        },
    ];

    const recognitionPhaseData = [
        {
            label: t(
                "tenantConfig.featureConfigurations.vectorVendorRecognitionPhaseSelection.labels.off"
            ),
            value: RecognitionPhase.Off,
        },
        {
            label: t(
                "tenantConfig.featureConfigurations.vectorVendorRecognitionPhaseSelection.labels.first"
            ),
            value: RecognitionPhase.First,
        },
        {
            label: t(
                "tenantConfig.featureConfigurations.vectorVendorRecognitionPhaseSelection.labels.last"
            ),
            value: RecognitionPhase.Last,
        },
    ];

    const getSearchServiceDropdownValues = (
        searchServices: ISearchService[]
    ): { label: string; value: string }[] => {
        return searchServices.map((searchService) => {
            return {
                label: `${searchService.name} (${searchService.indexCounter?.usage}/${searchService.indexCounter?.quota})`,
                value: searchService.name,
            };
        });
    };

    const getDisabledItemValues = (searchServices: ISearchService[]) => {
        return searchServices
            .filter((s) => s.indexCounter?.quota === s.indexCounter?.usage)
            .map((s) => s.name);
    };

    const featureDropdownTypeToSettingMapping = [
        {
            name: t("tenantConfig.featureConfigurations.mailerService.title"),
            value: "mailerService" as keyof Features,
            helpText: t(
                "tenantConfig.featureConfigurations.mailerService.description"
            ),
            selectedValue: features?.mailerService,
            changeHandler: handleTenantFeatureChange,
            data: mailerServiceData,
            group: TenantFeatureGroup.other,
            isAvailableForAx: true,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.ocrConfigurations.title"
            ),
            value: "ocrMethod" as keyof OcrConfiguration,
            helpText: t(
                "tenantConfig.featureConfigurations.ocrConfigurations.description"
            ),
            isAvailableForAx: true,
            data: ocrMethods,
            selectedValue: ocrConfiguration?.ocrMethod,
            placeholder: t(
                "tenantConfig.featureConfigurations.ocrConfigurations.placeholder"
            ),
            changeHandler: handleOcrConfigurationChange,
            group: TenantFeatureGroup.ocr,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.vectorVendorRecognitionPhaseSelection.title"
            ),
            value: "vectorVendorRecognitionPhaseSelection" as keyof RecognitionPhase,
            helpText: t(
                "tenantConfig.featureConfigurations.vectorVendorRecognitionPhaseSelection.description"
            ),
            isAvailableForAx: true,
            data: recognitionPhaseData,
            selectedValue: features?.vectorVendorRecognitionPhaseSelection,
            placeholder: t(
                "tenantConfig.featureConfigurations.vectorVendorRecognitionPhaseSelection.placeholder"
            ),
            changeHandler: handleTenantFeatureChange,
            disabled: isVendorSimilaritySearchDisabled,
            group: TenantFeatureGroup.vendorSimilaritySearch,
        },
    ];

    const featureButtonTypeToSettingMapping = [
        {
            name: t(
                "tenantConfig.featureConfigurations.modifySearchIndex.updateButton"
            ),
            disabled: isModifySearchDisabled || isSearchSettingsDisabled,
            helpText: t(
                "tenantConfig.featureConfigurations.modifySearchIndex.updateTooltip"
            ),
            clickHandler: handleUpdateClick,
            group: TenantFeatureGroup.search,
            isAvailableForAx: true,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.modifySearchIndex.recreateButton"
            ),
            disabled: isModifySearchDisabled || isSearchSettingsDisabled,
            helpText: t(
                "tenantConfig.featureConfigurations.modifySearchIndex.recreateTooltip"
            ),
            clickHandler: handleRecreateClick,
            group: TenantFeatureGroup.search,
            isAvailableForAx: true,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.modifyVendorSimilarity.seedButton"
            ),
            disabled: isVendorSimilaritySearchDisabled,
            helpText: t(
                "tenantConfig.featureConfigurations.modifyVendorSimilarity.seedTooltip"
            ),
            clickHandler: handleSeedClick,
            group: TenantFeatureGroup.vendorSimilaritySearch,
            isAvailableForAx: true,
        },
        {
            name: t(
                "tenantConfig.featureConfigurations.modifyVendorSimilarity.reindexButton"
            ),
            disabled: isVendorSimilaritySearchDisabled,
            helpText: t(
                "tenantConfig.featureConfigurations.modifyVendorSimilarity.reindexTooltip"
            ),
            clickHandler: handleReindexClick,
            group: TenantFeatureGroup.vendorSimilaritySearch,
            isAvailableForAx: true,
        },
    ];

    const filterSettings = (settings: any[], group: TenantFeatureGroup) => {
        if (customerType === CustomerType.AX2012) {
            settings = settings.filter((setting) => setting.isAvailableForAx);
        }

        return settings.filter((setting) => setting.group === group);
    };

    const expandAllGroups = () => {
        setExpandedStates(
            expandedStates.map(() =>
                expandedStates.some((state: boolean) => !state)
            )
        );
    };

    const expandGroup = (index: number) => {
        setExpandedStates((states) =>
            states.map((state, i) => (i === index ? !state : state))
        );
    };
    const getDropdownFeature = (setting: any, index: number) => {
        return (
            <Form.Group key={index} className={cn("feature-groups__field")}>
                <Form.ControlLabel className={"feature-groups__label"}>
                    {setting.name}
                    <Form.HelpText
                        className={"general-configurations__help"}
                        tooltip
                    >
                        {setting.helpText}
                    </Form.HelpText>
                </Form.ControlLabel>
                <Form.Control
                    placeholder={setting.placeholder}
                    name={setting.name}
                    accepter={SelectPicker}
                    disabled={setting.disabled ?? false}
                    data={setting.data}
                    value={setting.selectedValue}
                    searchable={false}
                    className={"feature-configurations__form-control"}
                    onChange={(value: unknown) => {
                        setting.changeHandler(setting.value, value);
                    }}
                    cleanable={false}
                />
            </Form.Group>
        );
    };

    const getInputFeature = (setting: any, index: number) => {
        return (
            <Form.Group key={index} className={cn("feature-groups__field")}>
                <Form.ControlLabel className={"feature-groups__label"}>
                    {setting.name}
                    {setting.microsoftLink && (
                        <a
                            className={
                                "feature-configurations__document-link-button"
                            }
                            href={setting.microsoftLink}
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            {t(
                                "tenantConfig.featureConfigurations.microsoftTitle"
                            )}
                        </a>
                    )}
                    {setting.helpText && (
                        <Form.HelpText
                            className={"general-configurations__help"}
                            tooltip
                        >
                            {setting.helpText}
                        </Form.HelpText>
                    )}
                </Form.ControlLabel>
                {setting.inputAddon ? (
                    <InputGroup
                        className={"feature-configurations__form-input-group"}
                    >
                        <Form.Control
                            className={
                                "feature-configurations__form-control-group"
                            }
                            accepter={setting.type}
                            name={setting.value}
                            value={setting.selectedValue}
                            disabled={setting.disabled ?? false}
                            onChange={(value: unknown) => {
                                setting.changeHandler(setting.value, value);
                            }}
                        />
                        {setting.inputAddon && (
                            <InputGroup.Addon
                                className={
                                    "feature-configurations__input-group-addon"
                                }
                            >
                                <div>{"min"}</div>
                            </InputGroup.Addon>
                        )}
                    </InputGroup>
                ) : (
                    <Form.Control
                        className={"general-configurations__form-control"}
                        accepter={setting.type}
                        name={setting.value}
                        value={setting.selectedValue}
                        disabled={setting.disabled ?? false}
                        onChange={(value: unknown) => {
                            setting.changeHandler(setting.value, value);
                        }}
                    />
                )}
            </Form.Group>
        );
    };

    const getButtonFeature = (setting: any, index: number) => {
        return (
            <HStack spacing={2} key={index}>
                <HStack.Item alignSelf={"center"}>
                    <Form.Control
                        name={setting.name}
                        accepter={Button}
                        className={cn(
                            "feature-configurations__general-btn",
                            "feature-configurations__modify-btn"
                        )}
                        onClick={setting.clickHandler}
                        disabled={setting.disabled}
                    >
                        {setting.name}
                    </Form.Control>
                </HStack.Item>

                <HStack.Item alignSelf={"center"}>
                    <Form.HelpText
                        className={"general-configurations__help"}
                        tooltip
                    >
                        {setting.helpText}
                    </Form.HelpText>
                </HStack.Item>
            </HStack>
        );
    };

    const getActiveSearchServiceName = (): string | null => {
        if (isSearchSettingsDisabled) {
            const workflowData = JSON.parse(
                searchServiceSelectionWorkflow?.data
            );

            return workflowData.name ?? linkedSearchService;
        }

        return linkedSearchService;
    };

    const handleTerminateRefresh = () => {
        setDataFetched(false);
    };

    const isSearchServiceAvailable = () => {
        return (
            searchServices
                .map((s) => s.indexCounter?.quota === s.indexCounter?.usage)
                .filter((s) => !s).length > 0
        );
    };

    const getSftpTableFeature = () => {
        return (
            <SftpIpAddressesContainer
                isSftpExpanded={true}
                isSftpEnabled={features?.sftpEnabled ?? false}
                sftpUserWithIpAddresses={sftpUserWithIpAddresses}
                areSftpAddressesValid={areSftpAddressesValid}
                disabledSettingNames={disabledSettingNames}
                enableSftpWorkflow={
                    featureSettingWorkFlows?.find(
                        (workflow) =>
                            workflow.type ===
                            TenantFeatureSettingWorkFlowType.EnableSftp
                    ) ?? null
                }
                setSftpAddressesValid={setSftpAddressesValid}
                setSftpUserWithIpAddresses={setSftpUserWithIpAddresses}
            />
        );
    };

    const getCheckboxFeature = (setting: any, index: number) => {
        return (
            <Form.Group
                key={index}
                className={cn(
                    "general-configurations__grid-item",
                    "feature-configurations__less-height"
                )}
            >
                <Checkbox
                    className={"feature-configurations__checkbox"}
                    value={setting.value}
                    checked={setting.isEnabled}
                    disabled={featureSettingWorkFlows === null}
                    onChange={
                        setting.changeHandler ?? handleTenantFeatureChange
                    }
                    key={index}
                >
                    <div>
                        {setting.name}
                        {setting.confluenceLink && (
                            <a
                                className={
                                    "feature-configurations__document-link-button"
                                }
                                href={setting.confluenceLink}
                                target="_blank"
                                rel="noreferrer noopener"
                            >
                                {t(
                                    "tenantConfig.featureConfigurations.confluenceTitle"
                                )}
                            </a>
                        )}

                        <Form.HelpText
                            tooltip
                            className={"feature-configurations__help-block"}
                        >
                            {setting.tooltip}
                        </Form.HelpText>
                    </div>
                </Checkbox>
            </Form.Group>
        );
    };

    const getSearchInstanceSection = () => {
        return (
            <>
                <Form.Group
                    className={cn(
                        "feature-groups__field",
                        "feature-groups__search-dropdown"
                    )}
                >
                    <Form.ControlLabel className={"feature-groups__label"}>
                        {t(
                            `tenantConfig.featureConfigurations.search.aISearchInstances`
                        )}
                    </Form.ControlLabel>
                    <Form.Control
                        name={"search-service"}
                        accepter={SelectPicker}
                        data={getSearchServiceDropdownValues(searchServices)}
                        value={getActiveSearchServiceName()}
                        placeholder={t(
                            `tenantConfig.featureConfigurations.search.aISearchInstancesPlaceholder`
                        )}
                        searchable={false}
                        disabledItemValues={getDisabledItemValues(
                            searchServices
                        )}
                        className={"feature-configurations__form-control"}
                        onChange={(value: unknown) => {
                            setIsUnsavedChangeAvailable(true);
                            setLinkedSearchService(value as string);
                        }}
                        cleanable={false}
                        disabled={isSearchSettingsDisabled}
                    />
                    {searchServiceSelectionWorkflow !== null && (
                        <ProcessModalButton
                            workflow={searchServiceSelectionWorkflow}
                            workflowType={WorkflowModalType.Feature}
                            triggerRefresh={handleTerminateRefresh}
                        />
                    )}
                </Form.Group>
                <div className={"feature-configurations__section"}>
                    {filterSettings(
                        featureButtonTypeToSettingMapping,
                        TenantFeatureGroup.search
                    ).map((setting, index) => {
                        return getButtonFeature(setting, index);
                    })}
                </div>
                {!isSearchServiceAvailable() && (
                    <Message
                        className={"feature-groups__search-index-warning"}
                        showIcon
                        type="warning"
                    >
                        {t(
                            `tenantConfig.featureConfigurations.search.availableSearchServicesMissing`
                        )}
                    </Message>
                )}
            </>
        );
    };

    return (
        <>
            <Button
                className={cn(
                    "feature-groups__button",
                    "feature-configurations__modify-btn"
                )}
                onClick={expandAllGroups}
            >
                <span>{buttonText}</span>
                <div>
                    {upperIcon}
                    {lowerIcon}
                </div>
            </Button>

            <div className="feature-groups__setting-groups">
                {TenantFeatureGroupsMapping.map((group, index) => {
                    const title = (
                        <div className={"feature-groups__group-title"}>
                            {t(
                                `tenantConfig.featureConfigurations.group.${TenantFeatureGroup[group]}`
                            )}
                        </div>
                    );

                    return (
                        <Collapsible
                            className="feature-groups__setting-group"
                            key={TenantFeatureGroup[group]}
                            title={title}
                            open={expandedStates[index]}
                            handleClick={() => {
                                expandGroup(index);
                            }}
                        >
                            <>
                                {group === TenantFeatureGroup.sftp &&
                                    getSftpTableFeature()}
                            </>
                            <>
                                {filterSettings(
                                    featureDropdownTypeToSettingMapping,
                                    group
                                ).map((setting, index) => {
                                    return getDropdownFeature(setting, index);
                                })}
                            </>
                            <>
                                {filterSettings(
                                    featureInputTypeToSettingMapping,
                                    group
                                ).map((setting, index) => {
                                    return getInputFeature(setting, index);
                                })}
                            </>
                            <>
                                {filterSettings(allWorkflows, group).map(
                                    (setting) => (
                                        <TenantFeatureSettingsCheckbox
                                            tenantFeatureSettingWorkFlows={
                                                featureSettingWorkFlows
                                            }
                                            tenantFeatureSettingWorkFlowType={
                                                setting.workflowType
                                            }
                                            disabledSettingNames={
                                                disabledSettingNames
                                            }
                                            disabled={setting.isDisabled}
                                            buttonText={setting.text}
                                            helpText={setting.helpText}
                                            value={setting.value}
                                            checked={setting.checked}
                                            onCheckboxChange={
                                                handleTenantFeatureChange
                                            }
                                            triggerRefresh={
                                                handleTerminateRefresh
                                            }
                                            key={setting.text}
                                        />
                                    )
                                )}
                            </>
                            <>
                                {filterSettings(
                                    featureFlagTypeToSettingMapping,
                                    group
                                ).map((setting, index) => {
                                    return getCheckboxFeature(setting, index);
                                })}
                            </>
                            <>
                                {group !== TenantFeatureGroup.search && (
                                    <div
                                        className={
                                            "feature-configurations__section"
                                        }
                                    >
                                        {filterSettings(
                                            featureButtonTypeToSettingMapping,
                                            group
                                        ).map((setting, index) => {
                                            return getButtonFeature(
                                                setting,
                                                index
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                            <>
                                {features.searchEnabled &&
                                    group === TenantFeatureGroup.search &&
                                    getSearchInstanceSection()}
                            </>
                        </Collapsible>
                    );
                })}
            </div>
        </>
    );
};

export default FeatureGroups;
